import type { NavMenuItem } from './types';
import { pathMatches } from './pathMatches';
import { menuItemsByRail } from './menuByRail';
import type { RailId } from './types';
import { RAIL_ORDER } from './rails';

const collectPaths = (items: NavMenuItem[]): { path: string; match?: NavMenuItem['match'] }[] =>
  items.map((item) => ({ path: item.path, match: item.match }));

/** Bookmark URL before the page moved under /inventory/report. */
const isLegacyInventoryReportPath = (p: string): boolean =>
  p === '/reports/inventory' || p.startsWith('/reports/inventory/');

const PATH_RAIL_RULES: { railId: RailId; test: (pathname: string) => boolean }[] = [
  {
    railId: 'marketing',
    test: (p) =>
      p === '/marketing' ||
      p.startsWith('/marketing/') ||
      p === '/promotions' ||
      p.startsWith('/promotions/') ||
      p === '/outbound' ||
      p.startsWith('/outbound/'),
  },
  {
    railId: 'reports',
    test: (p) => {
      if (isLegacyInventoryReportPath(p)) return false;
      return p === '/reports' || p.startsWith('/reports/') || p.startsWith('/work-timesheet');
    },
  },
  {
    railId: 'warehouse',
    test: (p) => {
      if (isLegacyInventoryReportPath(p)) return true;
      if (p === '/suppliers' || p.startsWith('/suppliers/')) return true;
      return p === '/inventory' || p.startsWith('/inventory/');
    },
  },
  {
    railId: 'catalog',
    test: (p) =>
      p.startsWith('/treatment-catalog') ||
      p.startsWith('/treatment-uoms') ||
      p.startsWith('/treatment-journals') ||
      p.startsWith('/drugs') ||
      p.startsWith('/prescription-templates') ||
      p.startsWith('/appointment-types') ||
      p.startsWith('/customer-sources') ||
      p.startsWith('/diagnosis-codes') ||
      p.startsWith('/labo/master') ||
      p.startsWith('/labo/partners') ||
      p.startsWith('/labo/catalog') ||
      p.startsWith('/income-categories') ||
      p.startsWith('/expense-categories') ||
      p.startsWith('/payment-methods'),
  },
  {
    railId: 'finance',
    test: (p) =>
      p.startsWith('/incomes') ||
      p.startsWith('/expenses') ||
      p.startsWith('/cashbook'),
  },
  {
    railId: 'appointments',
    test: (p) => p.startsWith('/appointments'),
  },
  {
    railId: 'patients',
    test: (p) =>
      p.startsWith('/patients') ||
      p.startsWith('/treatment-plans') ||
      p.startsWith('/treatment-sessions') ||
      p.startsWith('/prescriptions') ||
      p.startsWith('/labo/orders') ||
      p.startsWith('/payments'),
  },
  {
    railId: 'messaging',
    test: (p) => p.startsWith('/messaging'),
  },
  {
    railId: 'internalChat',
    test: (p) => p.startsWith('/internal-chat'),
  },
  {
    railId: 'leads',
    test: (p) => p.startsWith('/leads'),
  },
  {
    railId: 'work',
    test: (p) => p.startsWith('/tasks') || p.startsWith('/projects'),
  },
  {
    railId: 'hr',
    test: (p) =>
      p.startsWith('/staff') ||
      p.startsWith('/departments') ||
      pathMatches(p, '/attendance', 'attendanceList') ||
      p.startsWith('/overtime') ||
      p.startsWith('/kpi') ||
      p.startsWith('/scheduling') ||
      p.startsWith('/payroll') ||
      p.startsWith('/commissions') ||
      p.startsWith('/commission-rules'),
  },
  {
    railId: 'settings',
    test: (p) =>
      p.startsWith('/users') ||
      p.startsWith('/permission-groups') ||
      p.startsWith('/audit') ||
      p === '/automation' ||
      p.startsWith('/automation/') ||
      p.startsWith('/settings'),
  },
  {
    railId: 'overview',
    test: (p) => p.startsWith('/dashboard') || p === '/' || p === '',
  },
];

/**
 * Resolve which rail owns the current pathname.
 * Returns null for neutral paths (e.g. /profile) so the shell can keep the last rail.
 */
export const resolveRailId = (pathname: string): RailId | null => {
  for (const rule of PATH_RAIL_RULES) {
    if (rule.test(pathname)) return rule.railId;
  }

  for (const railId of RAIL_ORDER) {
    const paths = collectPaths(menuItemsByRail[railId]);
    if (
      paths.some(({ path, match }) => pathMatches(pathname, path, match ?? 'prefix'))
    ) {
      return railId;
    }
  }

  return null;
};
